/**
 * Customer-facing names and explanations for the two mining modes.
 *
 * PRESENTATION ONLY. The stored and wire values remain `solo` and `team_solo` -- they are
 * the database CHECK constraint on `orders.mining_mode`, the PowerPool request field, and
 * the vocabulary the backend domain owns (backend/src/lib/orderConfig.js). Renaming a
 * label must never rename a stored value, so the keys here are exactly those values and
 * only the right-hand side is Sidney's product language.
 *
 * The labels live in one module because they appear in four places -- the configurator,
 * the order summary, the order status page and the receipt email. Three copies of a
 * product name eventually disagree.
 *
 * SOURCE OF THE WORDING: supplied by Sidney as product owner, and reproduced.
 *
 * THE FEE QUALIFICATION IS DELIBERATE AND MUST NOT BE DROPPED. An earlier draft said the
 * "full block reward" is paid to the customer, which is the more dangerous error: it is
 * an absolute claim that there are no deductions, and Sidney has stated there are. The
 * copy now qualifies it without quantifying it.
 *
 * WHAT IS STILL UNKNOWN, AND MUST STAY UNSTATED: the fee itself -- its size, who charges
 * it, and whether it differs between the two modes. "Any applicable" is the honest
 * hedge; a percentage, an amount, or a worked example would be an invented commercial
 * term. Nothing in this application computes or subtracts a fee, and the order total is
 * the rental price only.
 */

export const MINING_MODE_LABELS: Record<string, string> = {
  solo: 'True Solo Mining',
  team_solo: 'Team Solo Mining',
};

/** The stored value for team mining. Named so no component compares a bare string. */
export const TEAM_SOLO_MODE = 'team_solo';

/**
 * The Metered Mining team on PowerPool, as supplied by Sidney.
 *
 * A LINK AND NOTHING ELSE. Nothing in this application reads this page, calls PowerPool,
 * or derives anything from it: Team Solo odds against the team's aggregate hashrate are
 * deliberately PARKED, and no figure on the storefront is computed from the team. The
 * odds a customer sees are calculated from their own rented hashrate exactly as they are
 * for True Solo.
 *
 * Recorded here beside the mode labels because it is a presentation fact about one
 * mining mode, and because a URL pasted into a component is a URL nobody can find later.
 */
export const POWERPOOL_TEAM_URL =
  'https://solo.powerpool.io/team/tm_82c9e665a6a6113fb7b7b0b77471517c';

export const MINING_MODE_DESCRIPTIONS: Record<string, string> = {
  solo:
    'Your rented hashrate mines independently. If it finds a block, the block reward is paid directly to your wallet, less any pool fees.',
  team_solo:
    'Your rented hashrate mines with the Metered Mining team, with block rewards split proportionally based on contributed work.',
};

/**
 * The words the team link wraps, which are already in the description above.
 *
 * SIDNEY ASKED FOR THE LINK INSIDE THE SENTENCE rather than on a line of its own. The
 * honest way to do that is to link words the copy already contains, so the sentence a
 * customer reads is unchanged and there is no second piece of copy to keep in step.
 */
export const TEAM_LINK_PHRASE = 'Metered Mining team';

/**
 * The Team Solo description, split around the phrase the link wraps.
 *
 * DERIVED, NOT DUPLICATED. The copy lives once, in MINING_MODE_DESCRIPTIONS; this only
 * says where to cut it. A second hand-written copy with the link baked in would be the
 * thing that eventually disagrees with the first.
 *
 * NULL IS A SUPPORTED ANSWER. If product ever rewords the description so the phrase is
 * no longer in it, the caller renders the plain description and the link simply does not
 * appear. A missing link is a small loss; mangled copy, or a crash on a storefront page,
 * is not.
 */
export function teamSoloDescriptionParts(): { before: string; link: string; after: string } | null {
  const text = MINING_MODE_DESCRIPTIONS[TEAM_SOLO_MODE] ?? '';
  const at = text.indexOf(TEAM_LINK_PHRASE);

  if (at === -1) return null;

  return {
    before: text.slice(0, at),
    link: TEAM_LINK_PHRASE,
    after: text.slice(at + TEAM_LINK_PHRASE.length),
  };
}

/** The display name for a stored mode value, falling back to the value itself. */
export function miningModeLabel(miningMode: string): string {
  return MINING_MODE_LABELS[miningMode] ?? miningMode;
}

/**
 * The short display name, for places where "Mining" is already implied.
 *
 * "True Solo" beside "100 TH/s · 30 days" in the orders list; "True Solo Mining" where
 * the mode is being chosen or explained. Same product name, one word shorter.
 *
 * DERIVED FROM THE CANONICAL LABEL RATHER THAN LISTED SEPARATELY. A second map would be
 * a second place to update when Sidney renames a mode, and the two would eventually
 * disagree -- which is the exact failure this module exists to prevent. A mode whose
 * label does not end in "Mining" simply keeps its full label, which is safe.
 */
export function miningModeShortLabel(miningMode: string): string {
  return miningModeLabel(miningMode).replace(/\s+Mining$/, '');
}
